import { useState, useRef, useEffect, useContext } from 'react';
import styles from './SalesReviewPage.module.css';
import { Container, Row, Col, Label, Input } from 'reactstrap';
import { ReactComponent as CloseIcon } from '../../assets/images/close.svg';
import { useHistory } from 'react-router';
import getClientContent from '../_BrandingProvider/brandContent';
import InfoTooltip from '../shared/InfoTooltip/InfoTooltip';
import SimpleReactValidator from 'simple-react-validator';
import { SalesContext } from '../contexts/SalesContext';
import { useCookies } from 'react-cookie';
import SpinnerButton from '../shared/SpinnerButton/SpinnerButton';
import useLoadAtTop from '../_ReactHooks/useLoadAtTop';
import { BrandingContext } from '../contexts/ContractContext';

export default function SalesReviewPage() {
    useLoadAtTop();
    const context = useContext(SalesContext);
    const branding = useContext(BrandingContext);
    const clientId = branding.clientId || 'AIG';
    const clientLower = clientId?.toLowerCase();
    const { variableText } = getClientContent(clientId);
    const clientPhone = variableText?.claim?.phone;
    const history = useHistory();
    const [cookies] = useCookies();
    const [zip, setZip] = useState(context.data?.deviceData?.zip || '');
    const [currentData, setCurrentData] = useState({});
    const [contract, setContract] = useState(null);
    const [taxAmount, setTaxAmount] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [, setForceUpdate] = useState(0);
    const contractNumber = context.data?.contract?.contractNumber || '';

    const validator = useRef(
        new SimpleReactValidator({
            messages: {
                required: ':attribute is required.'
            },
            element: (message) => (
                <div
                    className='errorMsg'
                    aria-live='polite'
                >
                    {message.charAt(0).toUpperCase() + message.slice(1)}
                </div>
            )
        })
    );

    useEffect(() => {
        if (!contractNumber) {
            getContractData();
        }

        if (context.data?.deviceData) {
            setCurrentData(context.data?.deviceData);
        }
    }, [clientId]);

    const getContractData = () => {
        fetch(`api/contract/create/${clientLower}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                XSRFTOKEN: cookies.XSRFTOKEN
            },
            body: JSON.stringify({
                contractId: 0,
                statusId: 5,
                channelId: 9,
                partyId: 0,
                countryCode: 'US'
            })
        }).then((res) => {
            if (res.ok) {
                res.json().then((data) => {
                    setContract(data);
                    context.updateContract(data);
                });
            } else {
                console.error(res.status);
            }
        });
    };

    const getTaxAmount = () => {
        fetch(`api/tax/calculate/${clientLower}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                XSRFTOKEN: cookies.XSRFTOKEN
            },
            body: JSON.stringify({
                calculateTaxFlag: true,
                confirmAddressFlag: false,
                currencyCode: 'USD',
                customer: {
                    address1: null,
                    city: null,
                    country: 'US',
                    state: null,
                    postalCode: zip
                },
                totalAmount: currentData?.planPrice,
                contractNumber: contract?.contractNumber || contractNumber
            })
        }).then((res) => {
            if (res.ok) {
                res.json().then((data) => {
                    setIsLoading(false);
                    setTaxAmount(data?.calculatedTaxTotalAmount);
                    context.updateDeviceData({
                        ...context.data?.deviceData,
                        zip: zip,
                        taxAmount: data?.calculatedTaxTotalAmount
                    });
                });
            } else {
                setIsLoading(false);
                console.error(res.status);
            }
        });
    };

    const handleTaxCalculation = () => {
        if (validator.current.allValid()) {
            setIsLoading(true);
            getTaxAmount();
        } else {
            validator.current.showMessages();
            setForceUpdate(1);
        }
    };

    const handleTaxInputChange = (e) => {
        const { value } = e.target;

        setZip(value);
    };

    const handleClearZip = () => {
        setZip('');
    };

    const handleCheckOut = () => {
        history.push({
            pathname: '/customerinfo',
            state: {
                contract: contract
            }
        });
    };

    const getTotalAmount = () => {
        const planPrice = parseFloat(currentData?.planPrice ?? 0);
        const tax = parseFloat(taxAmount ?? currentData?.taxAmount ?? 0);
        const total = planPrice + tax;

        return <span>${total.toFixed(2)}</span>;
    };

    const tax = taxAmount ?? currentData?.taxAmount;

    return (
        <Container className='pb-5'>
            <Row>
                <Col className={styles.reviewColContainer}>
                    <h3>Review Your Plan</h3>
                    <Row className={styles.amountRowContainers}>
                        <h4>{currentData?.activePlan?.riskProfileDescription}</h4>
                        <h4>${currentData?.planPrice?.toFixed(2)}</h4>
                    </Row>
                    <p>{currentData?.deviceInfo?.map((item) => item.assetName).join(', ')}</p>
                    <Row className={styles.amountRowContainers}>
                        <div className={styles.amountRowText}>
                            <p className='mb-0 mr-2'>Estimated Tax:</p>
                            <span className={styles.infoIcon}>
                                <InfoTooltip
                                    customClass={styles.infoText}
                                    infoType='questionInfo'
                                    message='Sales tax is only an estimate. Final sales tax will be calculated based on your address during check out.'
                                />
                            </span>
                        </div>
                        <p className='mb-0'>{tax !== null ? <span>${tax?.toFixed(2)}</span> : '---'}</p>
                    </Row>
                    <Row className='mt-3'>
                        <Col
                            sm='12'
                            md='7'
                            lg='6'
                        >
                            <div
                                className={
                                    validator.current.message('zip/postalCode', zip, 'required|numeric|size:5')
                                    && 'form-error'
                                }
                            >
                                <Label
                                    htmlFor='zip'
                                    className='form-label'
                                >
                                    Zip/Postal Code
                                </Label>
                                <span
                                    aria-hidden='true'
                                    className='required-indicator'
                                >
                                    *
                                </span>
                                <div className={styles.contentWrapper}>
                                    <div className={`datePicker ${styles.inputContainer}`}>
                                        <Input
                                            className={styles.zipInput}
                                            id='zip'
                                            name='zip'
                                            value={zip}
                                            onChange={handleTaxInputChange}
                                            aria-required='true'
                                            maxLength={5}
                                        />
                                        {zip && (
                                            <CloseIcon
                                                onClick={handleClearZip}
                                                className={styles.closeIcon}
                                            />
                                        )}
                                    </div>
                                    <SpinnerButton
                                        text='Apply'
                                        onClick={handleTaxCalculation}
                                        type='secondary'
                                        spinning={isLoading}
                                        disabled={isLoading}
                                    />
                                </div>
                                {validator.current.message('zip/postalCode', zip, 'required|numeric|size:5')}
                            </div>
                        </Col>
                    </Row>
                    <hr />
                    <Row className={styles.amountRowContainers}>
                        <h4>Total</h4>
                        <h4>{getTotalAmount()}</h4>
                    </Row>
                    <hr />
                    <Row className='mx-0 justify-content-end'>
                        <button
                            className='btn-primary'
                            onClick={handleCheckOut}
                        >
                            Check Out
                        </button>
                    </Row>
                    <hr />
                    <Row className='mx-0'>
                        <p className='mb-0'>
                            Need some help? Call <a href={`tel:${clientPhone}`}>{clientPhone}</a>
                        </p>
                    </Row>
                    <hr className='mb-0' />
                </Col>
            </Row>
        </Container>
    );
}
